import type { AppState } from "./types";
import type { RootState } from "./redux";
import { type PaneemType, PublicEndpoints } from "./constants";
import { getAppInfo, getTokenData } from "./functions";
import Cookies from "js-cookie";
import { registerAction, authenticateAction } from "./components/ImageCapture/ImageCaptureSlice";
import {
  checkStatusByIsraeliIdAction,
  checkStatusByRegNumAction,
  validateUserIsraeliIdAction,
  validateUserRegNumAction,
} from "./components/RegistrationPage/RegistrationPageSlice";
import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { confirmContactInfoAction, getContactInfoAction } from "./components/CompletionPage/ContactInfoSlice";
import { contries } from "./utility/contriesLidt";

const initialState: AppState = {
  error: null,
  success: null,
  isLoading: false,
  requestId: null,
  requestSource: "paneem",
  isValidated: false,
  isAuthentication: false,
  version: "",
  releaseDate: "",
  isUnderMaintenance: false,
  contries: contries,
  isCompleted: false,
  paneemStatus: null,
  isPaneemTypeSelected: false,
  paneemType: null,
  paneemStatusCheck: null,
  isEnabled: null,
};

export const getTokenDataAction = createAsyncThunk(PublicEndpoints.portal, async ({ token }: { token: string }) => {
  const response = await getTokenData(token);
  return response.data;
});

export const getAppInfoAction = createAsyncThunk(PublicEndpoints.info, async () => {
  await getAppInfo();
});

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setError: (state, { payload }: { payload: string | null }) => {
      state.error = payload;
    },

    setSuccess: (state, { payload }: { payload: string | null }) => {
      state.success = payload;
    },

    setIsLoading: (state, { payload }: { payload: boolean }) => {
      state.isLoading = payload;
    },

    setIsValidated: (state, { payload }: { payload: boolean }) => {
      state.isValidated = payload;
    },

    setIsAuthentication: (state, { payload }: { payload: boolean }) => {
      state.isAuthentication = payload;
    },

    setIsPaneemTypeSelected: (state, { payload }: { payload: PaneemType }) => {
      state.isPaneemTypeSelected = true;
      state.paneemType = payload;
    },

    resetPaneemStatusCheck: (state) => {
      state.paneemStatusCheck = null;
    },

    resetAppState: (state) => {
      return { ...initialState, releaseDate: state.releaseDate, version: state.version, isEnabled: state.isEnabled };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getAppInfoAction.fulfilled, (state) => {
      state.version = Cookies.get("version") || "?";
      state.releaseDate = Cookies.get("releaseDate") || "?";
      state.isUnderMaintenance = Cookies.get("isUnderMaintenance")
        ? (JSON.parse(Cookies.get("isUnderMaintenance") as "false" | "true") as boolean)
        : false;
      state.isEnabled = Cookies.get("isEnabled")
        ? (JSON.parse(Cookies.get("isEnabled") as "false" | "true") as boolean)
        : true;
    });

    builder.addCase(getContactInfoAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;
    });

    builder.addCase(getContactInfoAction.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.paneemStatus = null;
    });

    builder.addCase(getTokenDataAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.requestId = payload.requestId;
      state.requestSource = payload.requestSource;
      state.isValidated = true;
      state.isAuthentication = payload.isAuthenticated;
    });

    builder.addCase(registerAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;

      if (payload!.message === "document_back") return;
      else {
        state.paneemStatus = payload!.status as string;
        state.isCompleted = true;
      }
    });

    builder.addCase(authenticateAction.fulfilled, (state, { payload }) => {
      state.paneemStatus = payload!.status as string;
      state.isLoading = false;
      state.isCompleted = true;
    });

    builder.addMatcher(
      isAnyOf(validateUserRegNumAction.fulfilled, validateUserIsraeliIdAction.fulfilled),
      (state, { payload }) => {
        state.isLoading = false;
        state.isValidated = payload!.isValidated;
        state.isAuthentication = payload!.isAuthentication;
      }
    );

    builder.addMatcher(
      isAnyOf(checkStatusByRegNumAction.fulfilled, checkStatusByIsraeliIdAction.fulfilled),
      (state, { payload }) => {
        state.isLoading = false;
        state.paneemStatusCheck = payload!.status;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getTokenDataAction.rejected,
        registerAction.rejected,
        authenticateAction.rejected,
        getAppInfoAction.fulfilled,
        confirmContactInfoAction.rejected
      ),
      (state) => {
        state.isLoading = false;
      }
    );

    builder.addMatcher(
      isAnyOf(
        checkStatusByRegNumAction.rejected,
        checkStatusByIsraeliIdAction.rejected,
        validateUserRegNumAction.rejected,
        validateUserIsraeliIdAction.rejected
      ),
      (state, { payload }) => {
        state.isLoading = false;
        state.error = payload as string;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getTokenDataAction.pending,
        checkStatusByRegNumAction.pending,
        checkStatusByIsraeliIdAction.pending,
        validateUserRegNumAction.pending,
        validateUserIsraeliIdAction.pending,
        registerAction.pending,
        authenticateAction.pending,
        getAppInfoAction.pending,
        confirmContactInfoAction.pending
      ),
      (state) => {
        state.isLoading = true;
      }
    );

    builder.addMatcher(isAnyOf(confirmContactInfoAction.fulfilled), (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setIsLoading,
  setError,
  setSuccess,
  setIsValidated,
  setIsAuthentication,
  setIsPaneemTypeSelected,
  resetPaneemStatusCheck,
  resetAppState,
} = appSlice.actions;

export const selectError = (state: RootState) => state.app.error;
export const selectSuccess = (state: RootState) => state.app.success;
export const selectIsLoading = (state: RootState) => state.app.isLoading;
export const selectRequestId = (state: RootState) => state.app.requestId;
export const selectRequestSource = (state: RootState) => state.app.requestSource;
export const selectIsValidated = (state: RootState) => state.app.isValidated;
export const selectIsAuthentication = (state: RootState) => state.app.isAuthentication;
export const selectIsCompleted = (state: RootState) => state.app.isCompleted;
export const selectVersion = (state: RootState) => state.app.version;
export const selectReleaseDate = (state: RootState) => state.app.releaseDate;
export const selectIsUnderMaintenance = (state: RootState) => state.app.isUnderMaintenance;
export const selectPaneemStatus = (state: RootState) => state.app.paneemStatus;
export const selectContries = (state: RootState) => state.app.contries;
export const selectIsPaneemTypeSelected = (state: RootState) => state.app.isPaneemTypeSelected;
export const selectPaneemType = (state: RootState) => state.app.paneemType;
export const selectPaneemStatusCheck = (state: RootState) => state.app.paneemStatusCheck;
export const selectIsEnabled = (state: RootState) => state.app.isEnabled;

export default appSlice.reducer;
